import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Target, ArrowRight } from 'lucide-react';

import {
  FilterBuilder,
  SortableTh,
  TablePagination,
  applyFilterRules,
  countActiveRules,
  useColumnResize,
  usePagination,
  useTableSort,
  type FilterColumn,
  type FilterConjunction,
  type FilterRule,
  type SortAccessors,
} from '@/components/ui/data-table';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getWcLeads, type WcLead, type WcQuotable } from '@/services/whatconvertsService';
import { ConversionActionsPanel } from '@/features/ads/whatconverts/ConversionActionsPanel';
import { LeadDrawer } from '@/features/ads/whatconverts/LeadDrawer';
import { CorrectionControl, LabelCell, TeamMarker } from '@/features/ads/whatconverts/LeadLabelParts';
import { FollowUpPanel } from '@/features/ads/whatconverts/FollowUpPanel';
import { OutOfAreaPanel } from '@/features/ads/whatconverts/OutOfAreaPanel';
import { LeadLabelSummary } from '@/features/ads/whatconverts/LeadLabelSummary';
import {
  AI_LABELER,
  LABEL_BADGE,
  LABEL_TEXT,
  LEAD_LABEL_VIEWS,
  applyLabelView,
  extraLabelers,
  labelFrom,
  labelerName,
  labelsReady,
  type LeadLabelView,
} from '@/features/ads/whatconverts/leadLabels';
import { OutsideChangeBadge } from '@/features/ads/whatconverts/outsideChanges';
import {
  NEEDS_FOLLOW_UP,
  NOT_MARKED_YET,
  quotableNote,
} from '@/features/ads/whatconverts/quotableExplanation';
import { useTenantStore } from '@/stores/useTenantStore';

/**
 * CORE-W-03: date-range selector on the WhatConverts page. Options are
 * short-hand day counts (7 / 30 / 90 / 180) so the operator can flip
 * scope without a calendar picker. Default 90 days matches the previous
 * hardcoded window, so no bookmark loses meaning.
 *
 * URL sync mirrors the CORE-BP-04 pattern:
 *   - `?days=N` pins the selection on refresh + shared links.
 *   - `parseDaysParam` rejects unknown values so a stale link cannot
 *     force a broken read.
 *   - Default is dropped from the URL (?days=90 becomes bare) so the
 *     URL stays clean when the operator has not changed the window.
 */
export type WcDaysRange = 7 | 30 | 90 | 180;
export const WC_DAYS_OPTIONS: readonly {
  value: WcDaysRange;
  label: string;
}[] = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
];
export const WC_DAYS_URL_PARAM = 'days';
export const DEFAULT_WC_DAYS: WcDaysRange = 90;

export function parseDaysParam(raw: string | null | undefined): WcDaysRange | null {
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return (WC_DAYS_OPTIONS.find((o) => o.value === n)?.value as WcDaysRange | undefined) ?? null;
}

// CORE-W-01: the DEMO_ROWS / DEMO_LEADS / DEMO_SUMMARY fixtures — the
// "Mike Hammer Moving" fallback that shipped as production behaviour —
// were retired. Chans's spec §CORE-W-01 acceptance: no "sample data"
// banner in production; a tenant with no WhatConverts profile mapping
// gets the plain-English "Not configured" empty state below.

const QUOTABLE_META: Record<WcQuotable, { label: string; cls: string }> = {
  yes: { label: 'Quotable', cls: 'border-teal/30 bg-teal-light text-teal-dark' },
  pending: { label: 'Pending', cls: LABEL_BADGE.pending },
  no: { label: 'Not quotable', cls: 'border-coral/40 bg-coral-light text-coral-dark' },
  not_set: { label: 'Not set', cls: 'border-border bg-muted/50 text-muted-foreground' },
};

const LEAD_TYPE_LABEL: Record<string, string> = {
  phone_call: 'Phone call',
  web_form: 'Web form',
  chat: 'Chat',
  text_message: 'Text',
  email: 'Email',
};

function usd(v: number | null | undefined) {
  if (v == null) return '—';
  return `$${v.toLocaleString()}`;
}

function convState(l: WcLead): 'ready' | 'skip' | 'wait' {
  if (l.quotable === 'yes' && l.gclid) return 'ready';
  if (l.quotable === 'no') return 'skip';
  return 'wait';
}

function Stat({
  label,
  value,
  note,
  bar,
  barColor,
  final,
}: {
  label: string;
  value: number;
  note: React.ReactNode;
  bar: number;
  barColor: string;
  final?: boolean;
}) {
  return (
    <Card className={final ? 'border-teal/40 bg-teal-light/40' : ''}>
      <CardContent className="relative p-4">
        <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div
          className={`mt-1.5 text-3xl font-bold tabular-nums leading-none ${
            final ? 'text-teal-dark' : 'text-foreground'
          }`}
        >
          {value.toLocaleString()}
        </div>
        <div className="mt-2 text-xs text-muted-foreground">{note}</div>
        <div className="mt-3 h-1 overflow-hidden rounded bg-muted">
          <div className={`h-full rounded ${barColor}`} style={{ width: `${bar}%` }} />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Leads table plumbing — the same shared machinery as the search-terms table
// (sort, resize, pagination, rule filters), so the two ads tables behave
// identically under the operator's hands.
// ---------------------------------------------------------------------------

const LEAD_SORT_ACCESSORS: SortAccessors<WcLead> = {
  lead: (l) => l.contact_name ?? String(l.lead_id),
  type: (l) => l.lead_type,
  source: (l) => l.lead_source ?? '',
  quotable: (l) => l.quotable,
  ai: (l) => l.ai_label?.label ?? '',
  gclid: (l) => (l.gclid ? 1 : 0),
  value: (l) => l.quote_value,
  date: (l) => l.date_created ?? '',
};

const AI_FILTER_COLUMN: FilterColumn<WcLead> = {
  key: 'ai',
  label: 'Core AI',
  type: 'enum',
  accessor: (l) => l.ai_label?.label ?? 'none',
  options: [
    { value: 'yes', label: 'Quotable' },
    { value: 'no', label: 'Not quotable' },
    { value: 'pending', label: 'Pending' },
    { value: 'none', label: 'Not labelled yet' },
  ],
};

const LEAD_FILTER_COLUMNS: FilterColumn<WcLead>[] = [
  { key: 'lead', label: 'Lead', type: 'text', accessor: (l) => l.contact_name ?? String(l.lead_id) },
  { key: 'type', label: 'Type', type: 'text', accessor: (l) => l.lead_type },
  { key: 'source', label: 'Source', type: 'text', accessor: (l) => `${l.lead_source ?? ''} ${l.lead_medium ?? ''}` },
  {
    key: 'quotable',
    label: 'WhatConverts',
    type: 'enum',
    accessor: (l) => l.quotable,
    options: [
      { value: 'yes', label: 'Quotable' },
      { value: 'no', label: 'Not quotable' },
      { value: 'pending', label: 'Pending' },
      { value: 'not_set', label: 'Not set' },
    ],
  },
  { key: 'gclid', label: 'Click ID', type: 'text', accessor: (l) => l.gclid ?? '' },
  { key: 'value', label: 'Value', type: 'number', accessor: (l) => l.quote_value },
];

const LEAD_RESIZE_DEFAULTS = { lead: 180, type: 110, source: 150 };

export default function WhatConvertsLeadsPage() {
  // CORE-W-01: the leads read is tenant-scoped. The queryKey carries
  // tenantId so a client switch invalidates the cache and refetches;
  // profileId is not passed — the backend resolves the mapped profile
  // from tenant_id. Empty tenant disables the query so the page never
  // fires a request for "the default" client.
  //
  // CORE-W-04: tenantId also scopes the conversion-actions panel below.
  const tenantId = useTenantStore((s) => s.currentTenant);

  // CORE-W-03: date-range state. Initial value comes from the URL;
  // unknown values fall through to the 90-day default rather than
  // pinning a broken fetch.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlDays = parseDaysParam(searchParams.get(WC_DAYS_URL_PARAM));
  const [days, setDays] = useState<WcDaysRange>(urlDays ?? DEFAULT_WC_DAYS);
  const handleDaysChange = (value: WcDaysRange) => {
    setDays(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === DEFAULT_WC_DAYS) {
          next.delete(WC_DAYS_URL_PARAM);
        } else {
          next.set(WC_DAYS_URL_PARAM, String(value));
        }
        return next;
      },
      { replace: true },
    );
  };

  const q = useQuery({
    // CORE-W-03: queryKey carries `days` so a window change refetches.
    queryKey: ['whatconverts-leads', tenantId, days],
    queryFn: () => getWcLeads({ tenantId: tenantId ?? '', days }),
    enabled: Boolean(tenantId),
    retry: 0,
    refetchOnWindowFocus: false,
  });

  // CORE-W-01: no "sample data" fallback in production. A tenant with no
  // WhatConverts profile mapping raises a 404 from the backend; we render
  // an honest "not configured" state below, not the Mike Hammer demo
  // fixture. Empty tenantId (page opened without a workspace selected)
  // takes the same "not configured" path.
  // WhatConverts answers 410 "Invalid profile_id" for a profile it no
  // longer knows; the backend turns that into profile_status, a setup state.
  const profileNotRecognised = q.data?.profile_status === 'not_recognised';
  const isLive = Boolean(q.data?.leads) && !profileNotRecognised;
  const leads = useMemo(() => q.data?.leads ?? [], [q.data]);
  // Lead labels (shadow): on and readable for this response.
  const labelMeta = q.data?.lead_labels;
  const labelsOn = labelsReady(labelMeta);
  const otherLabelers = useMemo(() => extraLabelers(labelMeta), [labelMeta]);
  const [labelView, setLabelView] = useState<LeadLabelView>('all');
  const isNotConfigured =
    !tenantId ||
    (q.isError &&
      // Any error the backend raises for a missing mapping surfaces the
      // page's plain-English guidance. A network hiccup still shows the
      // guidance rather than a stale fixture — the tenant either has a
      // mapping or it does not.
      true);

  // CORE-W-02: the lead drawer opens when the operator clicks a row.
  // A single-row selection state — Chans's spec does not describe a
  // multi-select UX on leads, so the drawer is per-lead only.
  const [openLead, setOpenLead] = useState<WcLead | null>(null);

  // Table state: rule filters → sort → paginate, in that order, so the page
  // count always describes what the filter left.
  const [filterRules, setFilterRules] = useState<FilterRule[]>([]);
  const [conjunction, setConjunction] = useState<FilterConjunction>('and');
  const { sortKey, sortDir, toggleSort, sortRows } = useTableSort<WcLead>();
  const { widths, startResize } = useColumnResize(LEAD_RESIZE_DEFAULTS);

  const filterColumns = useMemo(
    () => (labelsOn ? [...LEAD_FILTER_COLUMNS.slice(0, 4), AI_FILTER_COLUMN, ...LEAD_FILTER_COLUMNS.slice(4)] : LEAD_FILTER_COLUMNS),
    [labelsOn],
  );
  const filteredLeads = useMemo(
    () =>
      applyFilterRules(
        labelsOn ? applyLabelView(leads, labelView) : leads,
        filterRules,
        filterColumns,
        conjunction,
      ),
    [leads, labelsOn, labelView, filterRules, filterColumns, conjunction],
  );
  const sortedLeads = useMemo(
    () => sortRows(filteredLeads, LEAD_SORT_ACCESSORS),
    [filteredLeads, sortRows],
  );
  const pagination = usePagination(sortedLeads.length, { defaultPageSize: 25 });
  const pageLeads = pagination.slice(sortedLeads);
  const activeRuleCount = countActiveRules(filterRules, filterColumns);

  const m = useMemo(() => {
    // CORE-W-01: no sample-data fallback. Compute from the live read, or
    // return zeroes when the tenant is not configured — the "Not
    // configured" empty state above tells the operator why.
    const total = leads.length;
    const yes = leads.filter((l) => l.quotable === 'yes').length;
    const withGclid = leads.filter((l) => l.gclid).length;
    const ready = leads.filter((l) => l.quotable === 'yes' && l.gclid).length;
    const dist: Record<WcQuotable, number> = { yes: 0, no: 0, pending: 0, not_set: 0 };
    leads.forEach((l) => (dist[l.quotable] = (dist[l.quotable] ?? 0) + 1));
    return { total, yes, withGclid, ready, dist };
  }, [leads]);

  const pct = (n: number) => (m.total ? Math.round((n / m.total) * 100) : 0);

  return (
    <div className="space-y-5 p-6">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Target className="h-5 w-5 text-teal" />
            WhatConverts leads
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            WhatConverts leads for this client, with WhatConverts' quotable mark and Core's own check side by side.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* CORE-W-01: the client name is the selected tenant, not a
              hardcoded fixture. If no workspace is picked, the "Client"
              chip renders the plain-English "not selected" state. */}
          <div className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium">
            <span className="text-muted-foreground">Client </span>
            {tenantId ?? 'not selected'}
          </div>
          {/* CORE-W-03: date-range selector. Bookmarks + shared links
              carry the operator's chosen window via `?days=N`. */}
          <div className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-2 py-1 text-sm font-medium">
            <span className="text-muted-foreground">Window</span>
            <Select
              value={String(days)}
              onValueChange={(v) => {
                const parsed = parseDaysParam(v);
                if (parsed !== null) handleDaysChange(parsed);
              }}
            >
              <SelectTrigger
                aria-label="Date range"
                className="h-7 border-0 bg-transparent px-1 py-0 text-sm font-medium shadow-none focus:ring-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WC_DAYS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Badge
            variant="outline"
            className={
              isLive
                ? 'border-teal/40 bg-teal-light text-teal-dark'
                : 'border-coral/40 bg-coral-light text-coral-dark'
            }
          >
            {q.isLoading
              ? 'Loading…'
              : profileNotRecognised
                ? 'Profile not recognised'
                : isLive
                  ? 'Live'
                  : 'Not configured'}
          </Badge>
        </div>
      </div>

      {/* CORE-W-01: honest "not configured" empty state — no sample data
          in production. Renders when no workspace is selected or the
          tenant has no WhatConverts profile mapping. */}
      {isNotConfigured && (
        <Card>
          <CardContent className="space-y-2 p-5 text-sm">
            <p className="font-medium">
              {tenantId
                ? 'This account has no WhatConverts profile connected.'
                : 'Pick a workspace to see its WhatConverts leads.'}
            </p>
            {tenantId ? (
              <p className="text-muted-foreground">
                Connect the client's WhatConverts profile on the Account
                Audit tab so leads can flow through Core. Until it is
                connected, this page has nothing to read; it does not
                fall back to another account's data.
              </p>
            ) : null}
          </CardContent>
        </Card>
      )}

      {profileNotRecognised && (
        <Card>
          <CardContent className="space-y-2 p-5 text-sm">
            <p className="font-medium">
              WhatConverts does not recognise the profile connected to this client
              {q.data?.profile_id ? ` (profile ${q.data.profile_id})` : ''}.
            </p>
            <p className="text-muted-foreground">
              The profile may have been removed or moved in WhatConverts. Connect the
              client's current WhatConverts profile on the Account Audit tab, and its
              leads show up here again.
            </p>
          </CardContent>
        </Card>
      )}

      {/* loop thesis */}
      <Card>
        <CardContent className="space-y-3 p-5">
          <p className="max-w-[72ch] text-sm">
            WhatConverts sends Google Ads a conversion for the leads it has marked{' '}
            <b className="text-coral-dark">quotable</b>, so bidding learns from those marks.
            When a mark is wrong, bidding learns from the wrong leads. Core checks each lead
            itself (calls from the transcript, forms from the form answers) and shows its answer
            next to WhatConverts' mark, so the team can see where they differ and correct them.
            For now Core's answers stay in Core: they do not change anything in WhatConverts or
            Google Ads.
          </p>
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="lead-flow">
            {[
              ['WhatConverts', 'calls · forms · chats'],
              ['core (this app)', 'classifies quotable (kept in Core for now)'],
              ['WhatConverts', 'quotable mark'],
              ['Google Ads', 'conversions, sent by WhatConverts'],
            ].map(([t, s], i, a) => (
              <div key={`${t}-${i}`} className="flex items-center gap-2">
                <div
                  className={`rounded-lg border px-3 py-2 ${
                    t.startsWith('core')
                      ? 'border-coral/45 bg-coral-light/40'
                      : 'border-border bg-muted/30'
                  }`}
                >
                  <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    {t}
                  </div>
                  <div className="text-xs text-foreground">{s}</div>
                </div>
                {i < a.length - 1 && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* funnel KPIs */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Stat label="Leads · window" value={m.total} note="All calls, forms & chats" bar={100} barColor="bg-coral" />
        <Stat
          label="Marked quotable"
          value={m.yes}
          note={`${pct(m.yes)}% — the real opportunities`}
          bar={pct(m.yes)}
          barColor="bg-teal"
        />
        <Stat
          label="Carry a click ID"
          value={m.withGclid}
          note="Traceable to a paid click"
          bar={pct(m.withGclid)}
          barColor="bg-teal/60"
        />
        <Stat
          final
          label="Quotable with a click ID"
          value={m.ready}
          note={
            <>
              Marked quotable in WhatConverts <b>and</b> carrying a Google click ID
            </>
          }
          bar={pct(m.ready)}
          barColor="bg-teal-dark"
        />
      </div>

      {/* distribution */}
      <Card>
        <CardContent className="p-5">
          <div className="mb-1 text-sm font-medium">Where the {m.total} leads stand</div>
          <p className="mb-3 text-xs text-muted-foreground">
            WhatConverts' own marks. “Not set” means nobody has marked them yet.
          </p>
          <div className="flex h-4 overflow-hidden rounded-md border border-border">
            <span className="bg-muted" style={{ width: `${pct(m.dist.not_set)}%` }} />
            <span className="bg-amber-400" style={{ width: `${pct(m.dist.pending)}%` }} />
            <span className="bg-teal" style={{ width: `${pct(m.dist.yes)}%` }} />
            <span className="bg-coral" style={{ width: `${pct(m.dist.no)}%` }} />
          </div>
          <div className="mt-2.5 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <i className="h-2.5 w-2.5 rounded-sm bg-teal" />
              <b className="text-foreground">{m.dist.yes}</b> quotable
            </span>
            <span className="flex items-center gap-1.5">
              <i className="h-2.5 w-2.5 rounded-sm bg-amber-400" />
              <b className="text-foreground">{m.dist.pending}</b> pending
            </span>
            <span className="flex items-center gap-1.5">
              <i className="h-2.5 w-2.5 rounded-sm bg-coral" />
              <b className="text-foreground">{m.dist.no}</b> not quotable
            </span>
            <span className="flex items-center gap-1.5">
              <i className="h-2.5 w-2.5 rounded-sm bg-muted" />
              <b className="text-foreground">{m.dist.not_set}</b> not set
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Lead labels in shadow: the summary strip. */}
      {labelsOn && tenantId ? <LeadLabelSummary tenantId={tenantId} days={days} /> : null}
      {labelsOn && tenantId ? <OutOfAreaPanel tenantId={tenantId} /> : null}
      {labelsOn && tenantId ? (
        <FollowUpPanel
          tenantId={tenantId}
          onOpenLead={(f) => {
            // The table may not hold an old lead; open it from what the list knows.
            setOpenLead(
              leads.find((x) => String(x.lead_id) === f.lead_id) ?? {
                lead_id: f.lead_id,
                lead_type: f.lead_type ?? 'unknown',
                quotable: f.wc_quotable ?? 'not_set',
                quote_value: null,
                gclid: null,
                lead_source: null,
                lead_medium: null,
                contact_name: f.contact_name,
                date_created: f.date_created,
              },
            );
          }}
        />
      ) : null}
      {labelMeta?.enabled && !labelMeta.available && isLive ? (
        <p className="text-xs text-muted-foreground">
          Core's lead labels are switched on but not set up on this deploy yet, so only
          WhatConverts' marks are shown.
        </p>
      ) : null}

      {/* leads table — shared data-table machinery: rule filters, sortable +
          resizable columns, sticky header + sticky lead column, pagination */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
          <div className="text-sm font-medium">
            Leads{' '}
            <span className="font-normal text-muted-foreground">
              — {sortedLeads.length.toLocaleString()}
              {activeRuleCount > 0
                ? ` matching ${activeRuleCount} filter${activeRuleCount === 1 ? '' : 's'}`
                : ''}{' '}
              of {leads.length.toLocaleString()}
            </span>
          </div>
          {/* CORE-W-02: this line used to claim names were shortened and
              that raw contact details never left WhatConverts. Both halves
              were false. The row below renders `contact_name` exactly as
              entered, and the leads endpoint returns contact name, email and
              phone — which this repo's own MCP proxy excludes the endpoint
              for, describing it as "the same class of customer PII".

              A false reassurance about personal data is worse than no
              reassurance, so it is deleted rather than reworded: inventing a
              different comforting sentence is how the first one happened.
              Whether the browser payload should carry email and phone at all
              (nothing renders them today) is a privacy call for Chans, filed
              as CORE-W-02.f rather than decided here.

              Guarded by features/ads/__tests__/noFalsePrivacyClaims.test.ts,
              which bans the shape of the claim, not one sentence. */}
        </div>
        {labelsOn ? (
          <div
            className="flex flex-wrap items-center gap-1.5 border-b border-border px-4 py-2"
            role="group"
            aria-label="Show leads"
          >
            {LEAD_LABEL_VIEWS.map((v) => (
              <Button
                key={v.value}
                type="button"
                variant="outline"
                aria-pressed={labelView === v.value}
                onClick={() => {
                  setLabelView(v.value);
                  pagination.setPage(1);
                }}
                className={`h-7 px-2.5 text-xs ${
                  labelView === v.value ? 'border-coral/45 bg-coral-light/40 text-foreground' : ''
                }`}
              >
                {v.label}
              </Button>
            ))}
            <span className="ml-1 text-xs text-muted-foreground">
              Disagreements: WhatConverts and Core AI both answered, differently.
            </span>
          </div>
        ) : null}
        <div className="border-b border-border px-4 py-2">
          <FilterBuilder
            columns={filterColumns}
            rules={filterRules}
            conjunction={conjunction}
            onRulesChange={setFilterRules}
            onConjunctionChange={setConjunction}
          />
        </div>
        <div className="max-h-[60vh] overflow-auto">
          <table className={`w-full text-sm ${labelsOn ? 'min-w-[1280px]' : 'min-w-[820px]'}`}>
            <thead className="sticky top-0 z-20 bg-warm-bg [&_th]:bg-warm-bg">
              <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <SortableTh
                  sortKey="lead"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                  resizable
                  width={widths.lead}
                  onResize={startResize}
                  className="sticky left-0 z-30 bg-warm-bg px-4 py-2 font-semibold"
                >
                  Lead
                </SortableTh>
                <SortableTh
                  sortKey="type"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                  resizable
                  width={widths.type}
                  onResize={startResize}
                  className="px-4 py-2 font-semibold"
                >
                  Type
                </SortableTh>
                <SortableTh
                  sortKey="source"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                  resizable
                  width={widths.source}
                  onResize={startResize}
                  className="px-4 py-2 font-semibold"
                >
                  Source
                </SortableTh>
                <SortableTh
                  sortKey="quotable"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                  className="px-4 py-2 font-semibold"
                >
                  WhatConverts
                </SortableTh>
                {labelsOn ? (
                  <SortableTh
                    sortKey="ai"
                    currentKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-4 py-2 font-semibold"
                  >
                    Core AI
                  </SortableTh>
                ) : null}
                {labelsOn
                  ? otherLabelers.map((name) => (
                      <th key={name} className="px-4 py-2 font-semibold">
                        {labelerName(name)}
                      </th>
                    ))
                  : null}
                {labelsOn ? <th className="px-4 py-2 font-semibold">Team check</th> : null}
                <SortableTh
                  sortKey="gclid"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                  className="px-4 py-2 font-semibold"
                >
                  Click ID
                </SortableTh>
                <SortableTh
                  sortKey="value"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                  className="px-4 py-2 text-right font-semibold"
                >
                  Value
                </SortableTh>
                <th className="px-4 py-2 font-semibold">Click match</th>
              </tr>
            </thead>
            <tbody>
              {pageLeads.map((l) => {
                const qm = QUOTABLE_META[l.quotable] ?? QUOTABLE_META.not_set;
                const st = convState(l);
                return (
                  <tr
                    key={String(l.lead_id)}
                    /* CORE-W-02: rows open the lead drawer on click.
                       Enter/Space are the keyboard equivalents so the
                       page stays operable without a mouse. */
                    role="button"
                    tabIndex={0}
                    aria-label={`Open lead ${l.contact_name || l.lead_id}`}
                    onClick={() => setOpenLead(l)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenLead(l);
                      }
                    }}
                    className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-coral-light/30 focus:bg-coral-light/40 focus:outline-none"
                  >
                    {/* Sticky lead column keeps the row identifiable while the
                        rest scrolls; solid bg so rows don't bleed through. */}
                    <td className="sticky left-0 z-10 bg-card px-4 py-2.5">
                      <div className="font-medium">{l.contact_name || LEAD_TYPE_LABEL[l.lead_type] || 'Lead'}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">#{l.lead_id}</div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {LEAD_TYPE_LABEL[l.lead_type] ?? l.lead_type}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {l.lead_source}
                      {l.lead_medium && l.lead_medium !== 'none' ? ` / ${l.lead_medium}` : ''}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant="outline"
                          className={qm.cls}
                          title={quotableNote(l) ?? undefined}
                        >
                          {qm.label}
                        </Badge>
                        {labelsOn ? (
                          <TeamMarker lead={l} answer={l.quotable === 'not_set' ? null : l.quotable} />
                        ) : null}
                        {labelsOn ? <OutsideChangeBadge count={l.outside_changes} /> : null}
                      </div>
                    </td>
                    {labelsOn ? (
                      <td className="px-4 py-2.5 align-top">
                        <LabelCell lead={l} label={labelFrom(l, AI_LABELER)} />
                      </td>
                    ) : null}
                    {labelsOn
                      ? otherLabelers.map((name) => (
                          <td key={name} className="px-4 py-2.5 align-top">
                            <LabelCell lead={l} label={labelFrom(l, name)} />
                          </td>
                        ))
                      : null}
                    {labelsOn && tenantId ? (
                      <td className="px-4 py-2.5 align-top">
                        {l.correction ? (
                          <div
                            className="mb-1 text-xs text-muted-foreground"
                            title={l.correction.note ?? undefined}
                          >
                            Team says{' '}
                            <span className="font-semibold text-foreground">
                              {LABEL_TEXT[l.correction.label]}
                            </span>
                            {l.correction.note ? ` · ${l.correction.note}` : ''}
                          </div>
                        ) : null}
                        <CorrectionControl lead={l} tenantId={tenantId} compact />
                      </td>
                    ) : null}
                    <td className="px-4 py-2.5">
                      {l.gclid ? (
                        <span className="font-mono text-[11px] text-teal-dark">{l.gclid}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{usd(l.quote_value)}</td>
                    <td className="px-4 py-2.5">
                      {st === 'ready' && (
                        <span className="text-xs font-semibold text-teal-dark">Quotable with a click ID</span>
                      )}
                      {st === 'skip' && <span className="text-xs text-muted-foreground">Not quotable</span>}
                      {/* Same words as the drawer note: a pending lead HAS
                          been marked, so it is not "awaiting score". */}
                      {st === 'wait' && (
                        <span className="text-xs text-muted-foreground">
                          {l.quotable === 'pending' ? NEEDS_FOLLOW_UP : NOT_MARKED_YET}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {sortedLeads.length > 0 && (
          <div className="border-t border-border px-4 py-2">
            <TablePagination
              page={pagination.page}
              pageCount={pagination.pageCount}
              pageSize={pagination.pageSize}
              totalRows={sortedLeads.length}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
      </Card>

      {/* CORE-W-04: Conversion Actions — the settings-level view of what
          Google Ads is measuring, moved off Management (spec §2.10) into
          WhatConverts as its home. Renders next to the lead list so the
          full conversion picture lives on one tab. */}
      <Card>
        <div className="border-b border-border px-4 py-3">
          <div className="text-sm font-medium">Conversion actions</div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            The actions Google Ads is measuring for this account. A conversion
            action that stops recording quietly makes every number that depends
            on it wrong.
          </p>
        </div>
        <div className="p-4">
          <ConversionActionsPanel tenantId={tenantId ?? null} />
        </div>
      </Card>

      {/* CORE-W-01: trailing status names only what shipped — no sample-
          data disclaimer, no future-tense promise. When the tenant has
          no WhatConverts profile the "Not configured" empty state above
          says so; there is no ambiguity here for the operator to resolve. */}
      {q.isLoading || isLive ? (
        <p className="flex items-center gap-2 pb-2 text-xs text-muted-foreground">
          {q.isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {isLive
            ? 'Live read from WhatConverts.'
            : 'Reading from WhatConverts…'}
        </p>
      ) : null}

      {/* CORE-W-02: lead detail drawer. Renders next to the leads table
          so an operator can click a row and see the full lead metadata
          + trace-back to the click that drove it. Editable value + stage
          land with W-02.b once the WhatConverts write endpoint is wired. */}
      <LeadDrawer
        lead={openLead && (leads.find((x) => String(x.lead_id) === String(openLead.lead_id)) ?? openLead)}
        onClose={() => setOpenLead(null)}
        tenantId={tenantId}
        labelsOn={labelsOn}
      />
    </div>
  );
}
